import { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppLayout } from '@/components/layout/AppLayout';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Plus,
  Clock,
  FolderOpen,
} from 'lucide-react';
import { useSubscription } from '@/utils/AuthContext';
import { cn } from '@/lib/utils';
import { chatLogger } from '@/utils/logger';
import { useChatHistory } from '@/hooks/useChatHistory';
import { useChatMessages } from '@/hooks/useChatMessages';
import { projectsApi } from '@/utils/api-pb';
import type { Project } from '@/utils/pocketbase';
import ChatHistorySidebar from '@/components/chat/ChatHistorySidebar';
import MessageInputArea from '@/components/chat/MessageInputArea';
import EmptyState from '@/components/chat/EmptyState';
import ArtifactCard from '@/components/chat/ArtifactCard';
import UpgradeCTACard from '@/components/chat/UpgradeCTACard';

import { useTranslation } from 'react-i18next';
const FREE_MESSAGE_LIMIT = 10;

// Функция для обработки inline-форматирования: жирный текст + ссылки
function processBoldText(text: string, keyPrefix: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  let partIndex = 0;

  // Combined regex: markdown links [text](url) OR bold **text** OR italic *text*
  const inlineRegex = /(\[([^\]]+)\]\(([^)]+)\))|(\*\*(.+?)\*\*)|(\*(.+?)\*)/g;
  let lastIndex = 0;
  let match;

  while ((match = inlineRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    if (match[1]) {
      // Markdown link: [text](url)
      const linkText = match[2]!;
      const linkUrl = match[3]!;
      if (linkUrl.startsWith('/')) {
        // Internal link → React Router
        parts.push(
          <Link key={`${keyPrefix}-${partIndex++}`} to={linkUrl} className="text-[var(--main-brand,#2197f1)] underline hover:opacity-80">
            {linkText}
          </Link>
        );
      } else {
        // External link
        parts.push(
          <a key={`${keyPrefix}-${partIndex++}`} href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-[var(--main-brand,#2197f1)] underline hover:opacity-80">
            {linkText}
          </a>
        );
      }
    } else if (match[4]) {
      // Bold: **text**
      const boldText = match[5]!;
      parts.push(
        <strong key={`${keyPrefix}-${partIndex++}`} className="font-semibold">
          {boldText}
        </strong>
      );
    } else if (match[6]) {
      // Italic: *text*
      const italicText = match[7]!;
      parts.push(
        <strong key={`${keyPrefix}-${partIndex++}`} className="font-semibold">
          {italicText}
        </strong>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  if (parts.length === 0) {
    parts.push(text);
  }

  return parts;
}

// Функция для парсинга markdown
function parseMarkdown(text: string): JSX.Element[] {
  const cleanText = text
    .replace(/```html\n?/gi, '')
    .replace(/```json\n?/gi, '')
    .replace(/```markdown\n?/gi, '')
    .replace(/```\n?/g, '')
    .replace(/^```$/gm, '')
    .trim();

  const lines = cleanText.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;

    if (line.startsWith('### ')) {
      elements.push(
        <h4 key={i} className="font-manrope font-semibold text-[14px] leading-[20px] text-[color:var(--black-text,#0d0d0d)] mt-3 mb-1">
          {processBoldText(line.substring(4), `h4-${i}`)}
        </h4>
      );
      i++;
    } else if (line.startsWith('## ')) {
      elements.push(
        <h3 key={i} className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)] mt-4 mb-2">
          {processBoldText(line.substring(3), `h3-${i}`)}
        </h3>
      );
      i++;
    } else if (line.startsWith('# ')) {
      elements.push(
        <h2 key={i} className="font-manrope font-bold text-[32px] leading-[40px] text-[color:var(--black-text,#0d0d0d)] mt-4 mb-3">
          {processBoldText(line.substring(2), `h2-${i}`)}
        </h2>
      );
      i++;
    } else if (line.startsWith('* ') || line.startsWith('- ') || /^\d+\.\s/.test(line)) {
      // Collect entire mixed list block (both numbered and bullet items)
      type BlockItem = { type: 'ol' | 'ul'; text: string; lineIdx: number };
      const block: BlockItem[] = [];
      const startIndex = i;

      while (i < lines.length) {
        const l = lines[i]!;
        if (/^\d+\.\s/.test(l)) {
          block.push({ type: 'ol', text: l.replace(/^\d+\.\s/, '').replace(/^\[[ x]\] /i, ''), lineIdx: i });
          i++;
        } else if (l.startsWith('* ') || l.startsWith('- ')) {
          block.push({ type: 'ul', text: l.substring(2).replace(/^\[[ x]\] /i, ''), lineIdx: i });
          i++;
        } else {
          break;
        }
      }

      const hasOl = block.some(b => b.type === 'ol');

      if (!hasOl) {
        // Pure unordered list
        elements.push(
          <ul key={`ul-${startIndex}`} className="list-disc pl-[24px] space-y-[8px] my-2 font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
            {block.map(item => (
              <li key={item.lineIdx} className="ml-1">
                {processBoldText(item.text, `li-${item.lineIdx}`)}
              </li>
            ))}
          </ul>
        );
      } else {
        // Has ordered items — group with nested sub-items
        type OlGroup = { text: string; lineIdx: number; subItems: { text: string; lineIdx: number }[] };
        const groups: OlGroup[] = [];
        const leadingUl: { text: string; lineIdx: number }[] = [];

        for (const item of block) {
          if (item.type === 'ol') {
            groups.push({ text: item.text, lineIdx: item.lineIdx, subItems: [] });
          } else {
            if (groups.length > 0) {
              groups[groups.length - 1]!.subItems.push({ text: item.text, lineIdx: item.lineIdx });
            } else {
              leadingUl.push({ text: item.text, lineIdx: item.lineIdx });
            }
          }
        }

        // Leading ul items before any ol
        if (leadingUl.length > 0) {
          elements.push(
            <ul key={`ul-lead-${startIndex}`} className="list-disc pl-[24px] space-y-[8px] my-2 font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
              {leadingUl.map(item => (
                <li key={item.lineIdx} className="ml-1">
                  {processBoldText(item.text, `li-${item.lineIdx}`)}
                </li>
              ))}
            </ul>
          );
        }

        // Ordered list with optional nested sub-items
        elements.push(
          <ol key={`ol-${startIndex}`} className="list-decimal pl-[24px] space-y-[8px] my-2 font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
            {groups.map(group => (
              <li key={group.lineIdx} className="ml-1">
                {processBoldText(group.text, `oli-${group.lineIdx}`)}
                {group.subItems.length > 0 && (
                  <ul className="list-disc pl-[20px] mt-[4px] space-y-[4px]">
                    {group.subItems.map(sub => (
                      <li key={sub.lineIdx} className="ml-1">
                        {processBoldText(sub.text, `li-${sub.lineIdx}`)}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        );
      }
    } else if (line === '') {
      elements.push(<br key={i} />);
      i++;
    } else {
      elements.push(
        <p key={i} className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
          {processBoldText(line, `text-${i}`)}
        </p>
      );
      i++;
    }
  }

  return elements;
}

export default function ChatPage() {
  const { t } = useTranslation('chat');
  const navigate = useNavigate();
  const { isPremium } = useSubscription();

  const { setNoPadding } = useAppLayout();

  useLayoutEffect(() => {
    setNoPadding(true);
    return () => setNoPadding(false);
  }, [setNoPadding]);

  const [input, setInput] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [artifactAttachments, setArtifactAttachments] = useState<import('@/components/chat/ArtifactSelectorDialog').ArtifactAttachment[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>();

  // Load projects for the project selector
  useEffect(() => {
    projectsApi.list().then((list) => {
      setProjects(list);
      if (list.length > 0) {
        setSelectedProjectId(list[0]!.id);
      }
    }).catch(() => {});
  }, []);

  const {
    chats,
    setChats,
    currentChat,
    setCurrentChat,
    isLoadingChats,
    totalMessageCount,
    setTotalMessageCount,
    chatToDelete,
    setChatToDelete,
    selectChat: selectChatFromHistory,
    createNewChat: createNewChatFromHistory,
    deleteChat,
    getChatTitle,
  } = useChatHistory();

  const isLimitReached = !isPremium && totalMessageCount >= FREE_MESSAGE_LIMIT;
  const canCreateNewChat = isPremium;

  const {
    messages,
    isLoading,
    messagesEndRef,
    handleSend: handleSendMessage,
  } = useChatMessages({
    currentChat,
    setCurrentChat,
    setChats,
    setTotalMessageCount,
    isLimitReached,
    projectId: selectedProjectId,
  });

  // Отладочный вывод состояния
  useEffect(() => {
    chatLogger.debug('State:', { isPremium, isLimitReached, totalMessageCount, currentChatId: currentChat?.id, messagesCount: messages.length });
  }, [isPremium, isLimitReached, totalMessageCount, currentChat, messages]);

  // Выбор чата (с закрытием истории)
  const selectChat = (chat: import('@/utils/api-pb').MentorChat) => {
    selectChatFromHistory(chat);
    setShowHistory(false);
  };

  // Создание нового чата (с закрытием истории)
  const createNewChat = async () => {
    if (!canCreateNewChat) return;
    const newChat = await createNewChatFromHistory();
    if (newChat) {
      setShowHistory(false);
    }
  };

  // Отправка сообщения
  const handleSend = async (messageText?: string) => {
    const artifacts = artifactAttachments.map((a) => ({ id: a.id, type: a.type }));
    const sent = await handleSendMessage(messageText, input, attachments, artifacts.length > 0 ? artifacts : undefined);
    if (sent) {
      setInput('');
      setAttachments([]);
      setArtifactAttachments([]);
    }
  };

  return (
    <>
      <div className="flex flex-col h-full relative">
        {/* Fixed Header */}
        <div className="sticky top-0 z-10 flex items-center gap-[8px] px-[24px] py-[12px]">
          {/* Project selector */}
          {projects.length > 0 && (
            <div className="flex items-center gap-[6px] mr-auto">
              <FolderOpen className="h-4 w-4 text-[var(--grey-text,#565858)]" />
              <select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value || undefined)}
                className="bg-transparent font-manrope font-semibold text-[13px] leading-[18px] text-[color:var(--black-text,#0d0d0d)] border-none outline-none cursor-pointer max-w-[200px] truncate"
              >
                <option value="">{t('header.noProject')}</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          <button
            className="flex items-center gap-[8px] px-[16px] py-[8px] rounded-[8px] text-[var(--grey-text,#565858)] hover:bg-[var(--grey-accent,#eeeeee)] transition-colors"
            onClick={() => setShowHistory(!showHistory)}
          >
            <Clock className="h-5 w-5" />
            <span className="hidden sm:inline font-manrope font-semibold text-[14px] leading-[20px]">{t('header.chatHistory')}</span>
          </button>

          {canCreateNewChat ? (
            <button
              className="flex items-center gap-[8px] px-[16px] py-[8px] rounded-[8px] text-[var(--grey-text,#565858)] hover:bg-[var(--grey-accent,#eeeeee)] transition-colors"
              onClick={createNewChat}
            >
              <Plus className="h-5 w-5" />
              <span className="hidden sm:inline font-manrope font-semibold text-[14px] leading-[20px]">{t('header.newChat')}</span>
            </button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="flex items-center gap-[8px] px-[16px] py-[8px] rounded-[8px] text-[var(--grey-text,#565858)] opacity-60 cursor-not-allowed"
                  disabled
                >
                  <Plus className="h-5 w-5" />
                  <span className="hidden sm:inline font-manrope font-semibold text-[14px] leading-[20px]">{t('header.newChat')}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {t('header.tooltip.newChatLimit')}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Chat area */}
        <div className="flex-1 overflow-y-auto">
            {messages.length === 0 && !isLoadingChats ? (
              <EmptyState
                isLimitReached={isLimitReached}
                onSend={(question) => handleSend(question)}
              />
            ) : (
              <div className="max-w-2xl mx-auto px-8 py-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={cn(
                      'mb-4',
                      message.role === 'user' ? 'flex justify-end' : ''
                    )}
                  >
                    {message.role === 'user' ? (
                      <div className="bg-white rounded-[24px] px-[16px] py-[8px] max-w-[85%]">
                        <p className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] whitespace-pre-wrap">
                          {message.content}
                        </p>
                      </div>
                    ) : (
                      <div className="max-w-[95%]">
                        <div className="prose prose-sm max-w-none">
                          {parseMarkdown(message.content)}
                        </div>
                        {message.artifacts && message.artifacts.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {message.artifacts.map((artifact, ai) => (
                              artifact.status === 'failed' ? (
                                <UpgradeCTACard key={ai} artifact={artifact} />
                              ) : (
                                <ArtifactCard
                                  key={ai}
                                  artifact={artifact}
                                  projectId={selectedProjectId}
                                />
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="mb-4">
                    <div className="flex gap-1.5 items-center py-3">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
        </div>

        {/* History sidebar */}
        {showHistory && (
          <ChatHistorySidebar
            chats={chats}
            currentChat={currentChat}
            chatToDelete={chatToDelete}
            setChatToDelete={setChatToDelete}
            canCreateNewChat={canCreateNewChat}
            onClose={() => setShowHistory(false)}
            onSelectChat={selectChat}
            onCreateNewChat={createNewChat}
            onDeleteChat={deleteChat}
            getChatTitle={getChatTitle}
          />
        )}

        {/* Limit reached card */}
        {isLimitReached && (
          <div className="mx-8 mb-4 border border-[var(--error-outline,#ff9196)] rounded-[16px] p-[24px]">
            <div className="text-center">
              <Clock className="h-8 w-8 mx-auto mb-[16px] text-[var(--grey-text,#565858)]" />
              <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)] mb-[8px]">
                {t('limitReached.title')}
              </h3>
              <p className="font-segoe text-[16px] leading-[24px] text-[color:var(--grey-text,#565858)] mb-[16px]">
                {t('limitReached.description')}
              </p>
              <button
                className="w-full bg-[var(--main-brand,#2197f1)] text-[color:var(--white,#ffffff)] px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] hover:opacity-90 transition-opacity"
                onClick={() => navigate('/subscription')}
              >
                {t('limitReached.button')}
              </button>
            </div>
          </div>
        )}

        {/* Fixed Input area at bottom */}
        <MessageInputArea
          input={input}
          setInput={setInput}
          attachments={attachments}
          setAttachments={setAttachments}
          artifactAttachments={artifactAttachments}
          setArtifactAttachments={setArtifactAttachments}
          isLoading={isLoading}
          isLimitReached={isLimitReached}
          onSend={() => handleSend()}
        />
      </div>
    </>
  );
}
